
import React, { useState, useEffect } from "react";
import {
  Grid,
  TextField,
  Button,
  Box,
  Snackbar,
  Alert,
  CircularProgress,
  Typography,
  Divider,
  IconButton,
  InputAdornment,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { getUser, login, loginWithOtp } from "../../../Redux/Auth/Action";
import axios from "axios";
import { useTheme } from "../../../contexts/ThemeContext";

export default function LoginUserForm({ handleNext, onSwitchToRegister }) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const jwt = localStorage.getItem("jwt");
  const { isDarkMode } = useTheme();

  const { auth } = useSelector((store) => store);

  const [openSnackBar, setOpenSnackBar] = useState(false);
  const [snackBarMessage, setSnackBarMessage] = useState("");
  const [snackBarSeverity, setSnackBarSeverity] = useState("success");
  const [forgotFlow, setForgotFlow] = useState(false);

  const [email, setEmail] = useState(""); // Will be used as identifier (Email or Mobile)
  const [otpInputs, setOtpInputs] = useState(["", "", "", "", "", ""]);
  const [emailVerified, setEmailVerified] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loginLoading, setLoginLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  
  // New States for OTP Login Flow
  const [isOtpLogin, setIsOtpLogin] = useState(false);
  const [loginOtpSent, setLoginOtpSent] = useState(false);

  // Helper function to check if input is mobile or email
  const isMobileNumber = (val) => /^[6-9]\d{9}$/.test(val);
  const isEmail = (val) => val.includes('@');
  const isValidIdentifier = (val) => isMobileNumber(val) || isEmail(val);

  const baseUrl = process.env.REACT_APP_API_BASE_URL;

  const textFieldStyles = {
    '& .MuiOutlinedInput-root': {
      backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.05)' : '#ffffff',
      '& fieldset': {
        borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.23)' : 'rgba(0, 0, 0, 0.23)',
      },
      '&:hover fieldset': {
        borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.4)' : 'rgba(0, 0, 0, 0.4)',
      },
      '&.Mui-focused fieldset': {
        borderColor: '#9155FD',
      },
    },
    '& .MuiInputLabel-root': {
      color: isDarkMode ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)',
    },
    '& .MuiInputBase-input': {
      color: isDarkMode ? 'white !important' : 'black',
      '&::placeholder': {
        color: isDarkMode ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.4)',
        opacity: 1,
      },
    },
  };


  const showSnackbar = (msg, severity = "success") => {
    setSnackBarMessage(msg);
    setSnackBarSeverity(severity);
    setOpenSnackBar(true);
  };

  const handleCloseSnackbar = () => setOpenSnackBar(false);

  useEffect(() => {
    if (jwt) {
      dispatch(getUser(jwt));
    }
  }, [jwt]);
  
useEffect(() => {
  if (auth.user && !auth.isLoading) {
    showSnackbar("Login Success", "success");
  }
}, [auth.user]);


const handleSubmit = async (event) => {
  event.preventDefault();
  setLoginLoading(true);

  if (isOtpLogin) {
    // Handle OTP Login Verification
    const otp = otpInputs.join("");
    if (otp.length < 6) {
      showSnackbar("Please enter a valid 6-digit OTP", "error");
      setLoginLoading(false);
      return;
    }

    try {
      const result = await dispatch(loginWithOtp(email, otp));
      if (!result?.success) {
        showSnackbar(auth.error || "Invalid OTP entered.", "error");
      }
    } finally {
      setLoginLoading(false);
    }
  } else {
    // Traditional Password Login
    const data = new FormData(event.currentTarget);
    const userData = {
      email: data.get("email"),
      password: data.get("password"),
    };

    try {
      await dispatch(login(userData));
      setTimeout(() => {
        if (auth.error && !localStorage.getItem('jwt')) {
          if (auth.error?.toLowerCase().includes("invalid")) {
            showSnackbar("You entered the wrong password.", "error");
          } else {
            showSnackbar(auth.error, "error");
          }
        }
      }, 100);
    } finally {
      setLoginLoading(false);
    }
  }
};

  const handleSendLoginOtp = async () => {
    if (!isValidIdentifier(email)) {
      showSnackbar("Please enter a valid mobile number or email for OTP login.", "error");
      return;
    }
    try {
      setLoading(true);
      const res = await axios.post(`${baseUrl}/auth/send-login-otp`, { identifier: email });
      showSnackbar(res.data.message, "success");
      setLoginOtpSent(true);
    } catch (err) {
      showSnackbar(err.response?.data?.message || "Account not found. Please sign up.", "error");
    } finally {
      setLoading(false);
    }
  };


  const handleSendOtp = async () => {
    try {
      setLoading(true);
      if (isMobileNumber(email)) {
        // Send WhatsApp OTP for Forgot Password
        const res = await axios.post(`${baseUrl}/auth/send-whatsapp-otp`, { mobile: email });
        showSnackbar(res.data.message, "success");
      } else {
        // Send Email OTP
        const res = await axios.post(`${baseUrl}/auth/send-reset-otp`, { email });
        showSnackbar(res.data.message, "success");
      }
      setOtpSent(true);
    } catch (err) {
      showSnackbar(err.response?.data?.message || "Failed to send OTP", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleOtpChange = (index, value) => {
    if (/^\d?$/.test(value)) {
      const updated = [...otpInputs];
      updated[index] = value;
      setOtpInputs(updated);
      if (value && index < 5) document.getElementById(`otp-${index + 1}`)?.focus();
    }
  };

  const handleVerifyOtp = async () => {
    const otp = otpInputs.join("");
    if (otp.length < 6) return showSnackbar("Enter a valid 6-digit OTP", "error");
    
    try {
      setLoading(true);
      let res;
      if (isMobileNumber(email)) {
        res = await axios.post(`${baseUrl}/auth/verify-whatsapp-otp`, { mobile: email, otp });
      } else {
        res = await axios.post(`${baseUrl}/auth/verify-otp`, { email, otp });
      }

      if (res.data.success) {
        showSnackbar("OTP verified", "success");
        setEmailVerified(true);
      }
    } catch (err) {
      showSnackbar(err.response?.data?.message || "Invalid OTP", "error");
    } finally {
      setLoading(false);
    }
  };

const handleResetPassword = async () => {
  try {
    const res = await axios.post(`${baseUrl}/auth/reset-password`, {
      email: email, // Backend identifier accepts both
      newPassword,
    });
    showSnackbar(res.data.message || "Password reset", "success");
    setForgotFlow(false);
    setEmailVerified(false);
    setOtpSent(false);
    setOtpInputs(["", "", "", "", ""]);
    setNewPassword("");
  } catch (err) {
    showSnackbar(err.response?.data?.message || "Reset failed", "error");
  }
};


  return (
    <React.Fragment>
      <Box sx={{ p: { xs: 0, md: 1 } }}>
        <Typography 
          variant="h5" 
          sx={{ 
            mb: 3, 
            fontWeight: 600,
            color: isDarkMode ? 'white' : 'black',
            textAlign: 'center'
          }}
        >
          {forgotFlow ? 'Reset Password' : 'Welcome Back'}
        </Typography>
        
        {!forgotFlow && (
          <Typography 
            variant="body2" 
            sx={{ 
              mb: 3, 
              color: isDarkMode ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)',
              textAlign: 'center'
            }}
          >
            Sign in to continue shopping
          </Typography>
        )}

      {!forgotFlow ? (
        <form className="w-full" onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                required
                id="email"
                name="email"
                label="Email or Mobile Number"
                fullWidth
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                sx={textFieldStyles}
              />
            </Grid>

            {isOtpLogin ? (
              // OTP Login Controls
              !loginOtpSent ? (
                <Grid item xs={12}>
                  <Button
                    fullWidth
                    variant="outlined"
                    onClick={handleSendLoginOtp}
                    disabled={loading || !isValidIdentifier(email)}
                    sx={{
                      padding: ".8rem 0",
                      borderColor: "#9155FD",
                      color: "#9155FD",
                      "&:hover": { borderColor: "#7E3FF2", backgroundColor: "rgba(145, 85, 253, 0.1)" },
                    }}
                  >
                    {loading ? <CircularProgress size={24} color="inherit" /> : `Get ${isMobileNumber(email) ? 'WhatsApp ' : ''}OTP`}
                  </Button>
                </Grid>
              ) : (
                <Grid item xs={12}>
                  <Typography variant="body2" sx={{ mb: 1, color: isDarkMode ? 'white' : 'black', textAlign: 'center' }}>
                    Enter OTP sent to {email}
                  </Typography>
                  <Box display="flex" justifyContent="center" gap={1}>
                    {otpInputs.map((digit, i) => (
                      <TextField
                        key={i}
                        id={`otp-${i}`}
                        value={digit}
                        onChange={(e) => handleOtpChange(i, e.target.value)}
                        inputProps={{
                          maxLength: 1,
                          style: { textAlign: "center" },
                        }}
                        sx={{
                          width: "50px",
                          ...textFieldStyles
                        }}
                      />
                    ))}
                  </Box>
                </Grid>
              )
            ) : (
              // Standard Password Input
              <Grid item xs={12}>
                <TextField
                  required
                  id="password"
                  name="password"
                  label="Password"
                  fullWidth
                  type={showPassword ? "text" : "password"}
                  sx={textFieldStyles}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                          sx={{ color: isDarkMode ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)' }}
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
            )}

            <Grid item xs={12}>
<Button
  className="bg-[#9155FD] w-full"
  type="submit"
  variant="contained"
  size="large"
  sx={{ 
    padding: ".8rem 0",
    background: 'linear-gradient(45deg, #9155FD 30%, #A67BFF 90%)',
    boxShadow: '0 3px 5px 2px rgba(145, 85, 253, .3)',
    '&:hover': {
      background: 'linear-gradient(45deg, #7E3FF2 30%, #9155FD 90%)',
      boxShadow: '0 4px 8px 3px rgba(145, 85, 253, .4)',
    }
  }}
  disabled={loginLoading}
>
  {loginLoading ? (
    <CircularProgress size={24} color="inherit" />
  ) : (
    "Sign In"
  )}
</Button>

            </Grid>
            <Grid item xs={12} display="flex" justifyContent="space-between" alignItems="center">
              <Button 
                size="small" 
                onClick={() => setForgotFlow(true)}
                sx={{ 
                  color: isDarkMode ? '#A67BFF' : '#9155FD',
                  textTransform: 'none',
                  '&:hover': {
                    backgroundColor: isDarkMode ? 'rgba(166, 123, 255, 0.1)' : 'rgba(145, 85, 253, 0.1)',
                  }
                }}
              >
                Forgot Password?
              </Button>

              {isMobileNumber(email) && (
                <Button 
                  size="small" 
                  onClick={() => {
                    setIsOtpLogin(!isOtpLogin);
                    setLoginOtpSent(false);
                    setOtpInputs(["", "", "", "", "", ""]);
                  }}
                  sx={{ 
                    color: isDarkMode ? '#A67BFF' : '#9155FD',
                    textTransform: 'none',
                    fontWeight: 'bold',
                    '&:hover': {
                      backgroundColor: isDarkMode ? 'rgba(166, 123, 255, 0.1)' : 'rgba(145, 85, 253, 0.1)',
                    }
                  }}
                >
                  {isOtpLogin ? "Login with Password" : "Login with OTP"}
                </Button>
              )}
            </Grid>
          </Grid>
        </form>
      ) : (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <TextField
              required
              label="Registered Email or Mobile"
              fullWidth
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              sx={textFieldStyles}
            />
          </Grid>

          {!otpSent && (
            <Grid item xs={12}>
              <Button 
                fullWidth 
                variant="contained" 
                onClick={handleSendOtp} 
                disabled={loading}
                sx={{ 
                  padding: ".8rem 0",
                  background: 'linear-gradient(45deg, #9155FD 30%, #A67BFF 90%)',
                  boxShadow: '0 3px 5px 2px rgba(145, 85, 253, .3)',
                }}
              >
                {loading ? <CircularProgress size={24} color="inherit" /> : "Send OTP"}
              </Button>
            </Grid>
          )}

          {otpSent && !emailVerified && (
            <>
              <Grid item xs={12}>
                <Box display="flex" justifyContent="center" gap={1}>
                  {otpInputs.map((digit, i) => (
                    <TextField
                      key={i}
                      id={`otp-${i}`}
                      value={digit}
                      onChange={(e) => handleOtpChange(i, e.target.value)}
                      inputProps={{
                        maxLength: 1,
                        style: { textAlign: "center" },
                      }}
                      sx={{ 
                        width: "50px",
                        ...textFieldStyles
                      }}
                    />
                  ))}
                </Box>
              </Grid>
              <Grid item xs={12}>
                <Button 
                  fullWidth 
                  variant="contained" 
                  onClick={handleVerifyOtp} 
                  disabled={loading}
                  sx={{ 
                    padding: ".8rem 0",
                    background: 'linear-gradient(45deg, #9155FD 30%, #A67BFF 90%)',
                  }}
                >
                  {loading ? <CircularProgress size={24} color="inherit" /> : "Verify OTP"}
                </Button>
              </Grid>
            </>
          )}

          {emailVerified && (
            <>
              <Grid item xs={12}>
                <TextField
                  label="New Password"
                  type={showNewPassword ? "text" : "password"}
                  fullWidth
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  sx={textFieldStyles}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowNewPassword(!showNewPassword)}
                          edge="end"
                          sx={{ color: isDarkMode ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)' }}
                        >
                          {showNewPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <Button 
                  fullWidth 
                  variant="contained" 
                  onClick={handleResetPassword}
                  sx={{ 
                    padding: ".8rem 0",
                    background: 'linear-gradient(45deg, #9155FD 30%, #A67BFF 90%)',
                  }}
                >
                  Set New Password
                </Button>
              </Grid>
            </>
          )}

          <Grid item xs={12}>
            <Button 
              size="small" 
              onClick={() => setForgotFlow(false)}
              sx={{ 
                color: isDarkMode ? '#A67BFF' : '#9155FD',
                textTransform: 'none',
              }}
            >
              Back to Login
            </Button>
          </Grid>
        </Grid>
      )}

      <Divider sx={{ my: 3, borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.12)' }} />

      <div className="flex justify-center flex-col items-center">
        <div className="py-3 flex items-center">
          <p className={`m-0 p-0 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
            Don't have an account?
          </p>
          <Button 
            onClick={onSwitchToRegister} 
            className="ml-5" 
            size="small"
            sx={{ 
              color: isDarkMode ? '#A67BFF' : '#9155FD',
              fontWeight: 600,
              textTransform: 'none',
              '&:hover': {
                backgroundColor: isDarkMode ? 'rgba(166, 123, 255, 0.1)' : 'rgba(145, 85, 253, 0.1)',
              }
            }}
          >
            Create Account
          </Button>
        </div>
      </div>
      </Box>

      <Snackbar open={openSnackBar} autoHideDuration={6000} onClose={handleCloseSnackbar}>
        <Alert onClose={handleCloseSnackbar} severity={snackBarSeverity} sx={{ width: "100%" }}>
          {snackBarMessage}
        </Alert>
      </Snackbar>
    </React.Fragment>
  );
}
